import React, { useEffect, useState, useMemo } from "react";
import Loader from "../../Shared/Loader";

import "../../../css/StationManagementTable.css";
import api from "../../../api/axiosConfig";
import AddStationModal from "./AddStationModal";
import ViewStationModal from "./ViewStationModal";
import EditStationModal from "./EditStationModal";

interface ApiListResponse<T> {
  success: boolean;
  data: T[];
  meta?: { total: number; page: number; pages: number; limit: number };
  message?: string;
}
interface ApiStation {
  _id: string;
  name: string;
  description?: string;
  role: string;
  roleName: string;
  subrole: string;
  subroleName: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  __v?: number;
}
type StationStatus = "active" | "inactive";
interface Station {
  id: string;
  type: string;
  description?: string;
  createdOn: string;
  disabledOn?: string;
  status: StationStatus;
  role: string;
  roleName: string;
  subrole: string;
  subroleName: string;
}

const StationManagementTable: React.FC = () => {
  const [stations, setStations] = useState<Station[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [selectedStation, setSelectedStation] = useState<Station | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [roleFilter, setRoleFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const toLocal = (iso?: string) =>
    iso ? new Date(iso).toLocaleDateString("en-GB") : "-";

  const fetchStations = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem("accessToken");
      const { data } = await api.center_api.get<ApiListResponse<ApiStation>>(
        "/api/v1/stations/getstations",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const items: ApiStation[] = data?.data ?? [];

      const mapped: Station[] = items.map((s: ApiStation) => ({
        id: s._id,
        type: s.name,
        description: s.description,
        createdOn: toLocal(s.createdAt),
        disabledOn: s.isActive ? undefined : toLocal(s.updatedAt),
        status: s.isActive ? "active" : "inactive",
        role: s.role,
        roleName: s.roleName,
        subrole: s.subrole,
        subroleName: s.subroleName,
      }));
      setStations(mapped);
    } catch (err) {
      console.error("Error fetching stations:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStations();
  }, []);

  const uniqueRoles = useMemo(
    () => Array.from(new Set(stations.map((s) => s.roleName))).filter(Boolean),
    [stations]
  );

  const filteredStations = useMemo(() => {
    return stations
      .filter((station) =>
        station.type.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .filter((station) => {
        if (statusFilter === "All") return true;
        return statusFilter === "Active"
          ? station.status === "active"
          : station.status === "inactive";
      })
      .filter((station) => {
        if (roleFilter === "All") return true;
        return station.roleName === roleFilter;
      });
  }, [stations, searchTerm, statusFilter, roleFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, roleFilter]);

  const totalPages = Math.ceil(filteredStations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentStationsToDisplay = filteredStations.slice(startIndex, endIndex);

  const handleStationAdded = () => fetchStations();
  const handleRowClick = (station: Station) => {
    setSelectedStation(station);
    setIsViewModalOpen(true);
  };
  const handleOpenEditModal = () => {
    setIsViewModalOpen(false);
    setIsEditModalOpen(true);
  };
  const handleStationUpdated = () => {
    setIsEditModalOpen(false);
    fetchStations();
  };

  // ---***---
  // 1. ADD THIS HANDLER
  // This function will be called by ViewStationModal after a successful enable/disable
  // ---***---
  const handleStationStatusToggled = () => {
    fetchStations(); // Just refresh the station list
  };

  return (
    <div className="station-table-container">
      <div className="station-header">
        <h2>Station Management</h2>
      </div>

      <div className="station-controls">
        <div className="global-filters-container" style={{ flex: 1 }}>
          <div className="global-search-wrapper">
            <svg
              className="global-search-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#94a3b8"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder="Search by station name..."
              className="global-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="global-select-input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="All">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
          <select
            className="global-select-input"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
          >
            <option value="All">All Roles</option>
            {uniqueRoles.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        </div>
        <button
          className="add-station-btn"
          onClick={() => setIsAddModalOpen(true)}
        >
          + Add Station
        </button>
      </div>

      <table className="station-table">
        <thead>
          <tr>
            <th>STATION TYPE</th>
            <th>Created On</th>
            <th>Disabled On</th>
            <th>Status</th>
            <th>Assigned Role</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={10}><Loader inline text="Loading Stations..." /></td>
            </tr>
          ) : currentStationsToDisplay.length > 0 ? (
            currentStationsToDisplay.map((s) => (
              <tr key={s.id} onClick={() => handleRowClick(s)}>
                <td>{s.type}</td>
                <td>{s.createdOn}</td>
                <td>{s.disabledOn || "-"}</td>
                <td>
                  <span className={`status-badge ${s.status}`}>
                    {s.status}
                  </span>
                </td>
                <td>{s.roleName}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={10}>No stations found.</td>
            </tr>
          )}
        </tbody>
      </table>

      <AddStationModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleStationAdded}
      />
      {/* ---***---
       // 2. PASS THE HANDLER AS A PROP
       // ---***--- */}
      <ViewStationModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        station={selectedStation}
        onEdit={handleOpenEditModal}
        onUpdateSuccess={handleStationStatusToggled} // <-- PROP ADDED
      />
      <EditStationModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        station={selectedStation}
        onSuccess={handleStationUpdated}
      />

      {totalPages > 0 && (
        <div className="pagination-footer">
          <span className="pagination-text">
            Showing {startIndex + 1} to{" "}
            {Math.min(endIndex, filteredStations.length)} of{" "}
            {filteredStations.length}
          </span>
          <div className="pagination-controls">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(
              (pageNumber) => (
                <button
                  key={pageNumber}
                  onClick={() => setCurrentPage(pageNumber)}
                  className={currentPage === pageNumber ? "active" : ""}
                >
                  {pageNumber}
                </button>
              )
            )}
            <button
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StationManagementTable;